import { flip$ } from '@/shared/assets/tokens';
import { FlipLabel } from '@/shared/components/molecules/FlipLabel';
import { StatPanel } from '@/shared/components/molecules/StatPanel';
import QuestionMarkTooltip from '@/shared/components/QuestionMarkTooltip';
import { type CastCacheValidatorFragment } from '@/shared/graphql/cache/fragments';
import useTokenPrice from '@/shared/hooks/useTokenPrice';
import AwardIcon from '@/shared/icons/flip-ui-kit/large/Award';
import CoinsIcon from '@/shared/icons/flip-ui-kit/large/Coins';
import GiftIcon from '@/shared/icons/flip-ui-kit/large/Gift';
import KnifeIcon from '@/shared/icons/flip-ui-kit/large/Knife';
import WithdrawIcon from '@/shared/icons/flip-ui-kit/large/Withdraw';
import { FLIP_SYMBOL } from '@/shared/utils/env';
import Flip from '@/shared/utils/FlipAmount';
import type TokenAmount from '@/shared/utils/TokenAmount';

type ValidatorStatPanelProps = {
  validators: Omit<CastCacheValidatorFragment, 'totalBalance'>[];
  isLoading: boolean;
  totalValidatorRewards: TokenAmount;
  totalValidatorRewardsEpoch: TokenAmount;
  totalValidatorSlashed: TokenAmount;
  totalValidatorsSlashedEpoch: TokenAmount;
};

const ValidatorStatPanel: React.FC<ValidatorStatPanelProps> = ({
  validators,
  isLoading,
  totalValidatorRewards,
  totalValidatorRewardsEpoch,
  totalValidatorSlashed,
  totalValidatorsSlashedEpoch,
}) => {
  const { price: flipUsdcPrice = 0 } = useTokenPrice(flip$);

  const totalBondedFlip = Flip.sum(validators.map((validator) => validator.lockedBalance.value));

  const totalBondedUsdc = totalBondedFlip.toBigNumber().multipliedBy(flipUsdcPrice).toString();

  const totalRedeemableFlip = Flip.sum(
    validators.map((validator) => validator.unlockedBalance.value),
  );

  const totalRedeemableUsdc = totalRedeemableFlip
    .toBigNumber()
    .multipliedBy(flipUsdcPrice)
    .toString();

  const totalBalanceFlip = totalBondedFlip.add(totalRedeemableFlip);
  const totalBalanceUsdc = totalBalanceFlip?.toBigNumber().multipliedBy(flipUsdcPrice).toString();

  const totalRewardsEpochFlip = totalValidatorRewardsEpoch.toFixed(2);

  const totalSlashedEpochFlip = totalValidatorsSlashedEpoch.toFixed(2);

  return (
    <div className="grid grid-cols-1 gap-x-10 gap-y-5 md:grid-cols-2  lg:auto-rows-fr lg:grid-cols-3">
      <StatPanel isLoading={isLoading}>
        <div className="flex items-center space-x-2">
          <CoinsIcon className="text-cf-green-1" />
          <div className="flex items-center text-14 text-white sm:text-20">
            <span className="mr-[5px]">Total Balance</span>
            <QuestionMarkTooltip
              content={`The total amount of ${FLIP_SYMBOL} funded across all your validator nodes.`}
            />
          </div>
        </div>
        <FlipLabel amount={totalBalanceFlip} usdAmount={totalBalanceUsdc} />
      </StatPanel>
      <StatPanel isLoading={isLoading}>
        <div className="flex items-center space-x-2">
          <AwardIcon className="text-cf-green-1" />
          <div className="flex items-center text-14 text-white sm:text-20">
            <span className="mr-[5px]">Total Bonded</span>
            <QuestionMarkTooltip
              content={`The total amount of ${FLIP_SYMBOL} bonded across all validator nodes in the current Authority Set.`}
            />
          </div>
        </div>
        <FlipLabel amount={totalBondedFlip} usdAmount={totalBondedUsdc} />
      </StatPanel>
      <StatPanel isLoading={isLoading}>
        <div className="flex items-center space-x-2">
          <WithdrawIcon className="text-cf-green-1" />
          <div className="flex items-center text-14 text-white sm:text-20">
            <span className="mr-[5px]">Available for redemption</span>
            <QuestionMarkTooltip
              content={`The total amount of ${FLIP_SYMBOL} that isn't bonded and available for redemption across all validator nodes.`}
            />
          </div>
        </div>
        <FlipLabel amount={totalRedeemableFlip} usdAmount={totalRedeemableUsdc} />
      </StatPanel>
      <StatPanel isLoading={isLoading}>
        <div className="flex items-center space-x-2">
          <GiftIcon className="text-cf-green-1" />
          <div className="flex items-center text-14 text-white sm:text-20">
            <span className="mr-[5px]">Total Rewards</span>
            <QuestionMarkTooltip
              content={`The total amount of ${FLIP_SYMBOL} rewards earned across all of your validator nodes.`}
            />
          </div>
        </div>
        <div className="flex flex-col items-end">
          <FlipLabel amount={totalValidatorRewards} large hideSymbol />
          <span className="ml-auto text-10">
            {totalRewardsEpochFlip} {FLIP_SYMBOL} this epoch
          </span>
        </div>
      </StatPanel>
      <StatPanel isLoading={isLoading}>
        <div className="flex items-center space-x-2">
          <KnifeIcon className="text-cf-green-1" />
          <div className="flex items-center text-14 text-white sm:text-20">
            <span className="mr-[5px]">Total Slashed</span>
            <QuestionMarkTooltip
              content={`The total amount of confiscated funds in ${FLIP_SYMBOL}  across all validator nodes due to failing to fulfil its duties. These funds are burned as a penalty.`}
            />
          </div>
        </div>
        <div className="flex flex-col items-end">
          <FlipLabel amount={totalValidatorSlashed} large hideSymbol />
          <span className="ml-auto text-10">
            ~{totalSlashedEpochFlip} {FLIP_SYMBOL} this epoch
          </span>
        </div>
      </StatPanel>
    </div>
  );
};

export default ValidatorStatPanel;
